import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Alert, MenuItem, IconButton, Chip, Card, CardContent, Stack, TablePagination } from '@mui/material';
import { Edit, Delete, Business, Palette } from '@mui/icons-material';
import { clientService } from '../services/api';
import { usePagination } from '../hooks/usePagination';

const TenantManagement: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '', description: '', contactEmail: '', contactPhone: '',
    subdomain: '', companyAddress: '', companyWebsite: '',
    primaryColor: '#1976d2', secondaryColor: '#dc004e',
    subscriptionPlan: 'Basic', maxEmployees: 50
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const data = await clientService.getClients();
      setTenants(data);
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  };

  const handleCreate = async () => {
    try {
      await clientService.createClient(formData);
      setMessage('Tenant created successfully');
      setOpen(false);
      loadTenants();
      resetForm();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error creating tenant');
    }
  };

  const handleEdit = (tenant: any) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      description: tenant.description,
      contactEmail: tenant.contactEmail,
      contactPhone: tenant.contactPhone,
      subdomain: tenant.subdomain || '',
      companyAddress: tenant.companyAddress || '',
      companyWebsite: tenant.companyWebsite || '',
      primaryColor: tenant.primaryColor || '#1976d2',
      secondaryColor: tenant.secondaryColor || '#dc004e',
      subscriptionPlan: tenant.subscriptionPlan || 'Basic',
      maxEmployees: tenant.maxEmployees || 50
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedTenant) return;
    try {
      await clientService.updateClient(selectedTenant.id, { ...formData, isActive: selectedTenant.isActive });
      setMessage('Tenant updated successfully');
      setEditOpen(false);
      loadTenants();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error updating tenant');
    }
  };

  const handleDelete = async () => {
    if (!selectedTenant) return;
    try {
      await clientService.deleteClient(selectedTenant.id);
      setMessage('Tenant deleted successfully');
      setDeleteOpen(false);
      loadTenants();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error deleting tenant');
    }
  };

  const handleBranding = (tenant: any) => {
    setSelectedTenant(tenant);
    setFormData({
      ...formData,
      primaryColor: tenant.primaryColor || '#1976d2',
      secondaryColor: tenant.secondaryColor || '#dc004e'
    });
    setBrandingOpen(true);
  };

  const handleUpdateBranding = async () => {
    if (!selectedTenant) return;
    try {
      await clientService.updateBranding(selectedTenant.id, {
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor
      });
      setMessage('Branding updated successfully');
      setBrandingOpen(false);
      loadTenants();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error updating branding');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', contactEmail: '', contactPhone: '',
      subdomain: '', companyAddress: '', companyWebsite: '',
      primaryColor: '#1976d2', secondaryColor: '#dc004e',
      subscriptionPlan: 'Basic', maxEmployees: 50
    });
  };

  const { pagedRows: pagedTenants, paginationProps: tenantsPaginationProps } = usePagination(tenants);

  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.isActive).length,
    inactive: tenants.filter(t => !t.isActive).length,
    totalEmployees: tenants.reduce((sum, t) => sum + (t.maxEmployees || 0), 0)
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
        Tenant Management
      </Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Total Tenants</Typography>
            <Typography variant="h4">{stats.total}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Active</Typography>
            <Typography variant="h4" color="success.main">{stats.active}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Inactive</Typography>
            <Typography variant="h4" color="error.main">{stats.inactive}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Total Capacity</Typography>
            <Typography variant="h4">{stats.totalEmployees}</Typography>
          </CardContent>
        </Card>
      </Box>

      <Button variant="contained" onClick={() => setOpen(true)} sx={{ mb: 2 }}>
        Create New Tenant
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Subdomain</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Max Employees</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedTenants.map((tenant) => (
              <TableRow key={tenant.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tenant.primaryColor || '#1976d2' }} />
                    <Typography variant="body2" fontWeight="bold">{tenant.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={tenant.subdomain || 'N/A'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{tenant.contactEmail}</Typography>
                  <Typography variant="caption" color="textSecondary">{tenant.contactPhone}</Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={tenant.subscriptionPlan || 'Basic'} 
                    size="small" 
                    color={tenant.subscriptionPlan === 'Enterprise' ? 'primary' : tenant.subscriptionPlan === 'Premium' ? 'secondary' : 'default'}
                  />
                </TableCell>
                <TableCell>{tenant.maxEmployees || 50}</TableCell>
                <TableCell>
                  <Chip 
                    label={tenant.isActive ? 'Active' : 'Inactive'} 
                    size="small" 
                    color={tenant.isActive ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEdit(tenant)} title="Edit">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleBranding(tenant)} title="Branding">
                    <Palette fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => { setSelectedTenant(tenant); setDeleteOpen(true); }} title="Delete">
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {tenants.length > 0 && <TablePagination {...tenantsPaginationProps} />}
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Company Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <TextField fullWidth label="Subdomain *" value={formData.subdomain} onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })} helperText="e.g., acme (acme.hrms.com)" />
            </Box>
            <TextField fullWidth label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Contact Email *" type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} />
              <TextField fullWidth label="Contact Phone" value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} />
            </Box>
            <TextField fullWidth label="Company Address" value={formData.companyAddress} onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Company Website" value={formData.companyWebsite} onChange={(e) => setFormData({ ...formData, companyWebsite: e.target.value })} />
              <TextField select fullWidth label="Subscription Plan" value={formData.subscriptionPlan} onChange={(e) => setFormData({ ...formData, subscriptionPlan: e.target.value })}>
                <MenuItem value="Basic">Basic (Free)</MenuItem>
                <MenuItem value="Premium">Premium</MenuItem>
                <MenuItem value="Enterprise">Enterprise</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Max Employees" type="number" value={formData.maxEmployees} onChange={(e) => setFormData({ ...formData, maxEmployees: parseInt(e.target.value) })} />
              <TextField fullWidth label="Primary Color" type="color" value={formData.primaryColor} onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Tenant</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Company Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              <TextField fullWidth label="Subdomain" value={formData.subdomain} onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })} disabled />
            </Box>
            <TextField fullWidth label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Contact Email" type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} />
              <TextField fullWidth label="Contact Phone" value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} />
            </Box>
            <TextField fullWidth label="Company Address" value={formData.companyAddress} onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth label="Company Website" value={formData.companyWebsite} onChange={(e) => setFormData({ ...formData, companyWebsite: e.target.value })} />
              <TextField select fullWidth label="Subscription Plan" value={formData.subscriptionPlan} onChange={(e) => setFormData({ ...formData, subscriptionPlan: e.target.value })}>
                <MenuItem value="Basic">Basic (Free)</MenuItem>
                <MenuItem value="Premium">Premium</MenuItem>
                <MenuItem value="Enterprise">Enterprise</MenuItem>
              </TextField>
            </Box>
            <TextField fullWidth label="Max Employees" type="number" value={formData.maxEmployees} onChange={(e) => setFormData({ ...formData, maxEmployees: parseInt(e.target.value) })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Branding Dialog */}
      <Dialog open={brandingOpen} onClose={() => setBrandingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Branding - {selectedTenant?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField fullWidth label="Primary Color" type="color" value={formData.primaryColor} onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Secondary Color" type="color" value={formData.secondaryColor} onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })} />
            <Box sx={{ mt: 3, p: 2, border: '1px solid #e2e8f0', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Preview</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ width: 60, height: 60, bgcolor: formData.primaryColor, borderRadius: 1 }} />
                <Box sx={{ width: 60, height: 60, bgcolor: formData.secondaryColor, borderRadius: 1 }} />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrandingOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateBranding} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{selectedTenant?.name}"? This will remove all associated data.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantManagement;
